if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.container = this.querySelector('[role="document"]');
        this.mediaList = [];
        this.currentIndex = 0;
        this.pendingActiveMedia = null;
        this.instantScrollRestoreTimer = null;
        this.instantScrollSnapshot = null;

        this.bindBackgroundClose();
        this.bindRightClickProtection();
      }

      getSectionId() {
        if (this.id && this.id.indexOf('ProductModal-') === 0) {
          return this.id.replace('ProductModal-', '');
        }

        const firstMedia = this.querySelector('[data-media-id]');
        const firstMediaId = firstMedia ? firstMedia.getAttribute('data-media-id') : '';

        if (firstMediaId) {
          return firstMediaId.replace(/-\d+$/, '');
        }

        return '';
      }

      getLinkedGallery(opener) {
        if (opener && opener.closest) {
          const galleryFromOpener = opener.closest('media-gallery');
          if (galleryFromOpener) return galleryFromOpener;
        }

        const sectionId = this.getSectionId();

        if (sectionId) {
          const galleryById = document.getElementById('MediaGallery-' + sectionId);
          if (galleryById) return galleryById;
        }

        return document.querySelector('media-gallery');
      }

      getRawMediaId(mediaId) {
        if (!mediaId) return '';

        const cleanId = String(mediaId).trim();
        const parts = cleanId.split('-');

        return parts[parts.length - 1] || cleanId;
      }

      getMediaIdFromElement(element) {
        if (!element || !element.getAttribute) return '';

        const ownMediaId = element.getAttribute('data-media-id');
        if (ownMediaId) return ownMediaId;

        if (element.closest) {
          const closestMedia = element.closest('[data-media-id]');
          if (closestMedia) {
            return closestMedia.getAttribute('data-media-id') || '';
          }
        }

        return '';
      }

      getActiveMediaIdFromGallery(opener) {
        const gallery = this.getLinkedGallery(opener);
        if (!gallery) return '';

        const sectionId = this.getSectionId();

        const activeSlide =
          gallery.querySelector('[id^="Slider-Gallery"] [data-media-id].is-active') ||
          gallery.querySelector('.product__media-item.is-active[data-media-id]');

        if (activeSlide) {
          const activeSlideId = activeSlide.getAttribute('data-media-id');
          if (activeSlideId) return activeSlideId;
        }

        const activeThumbnailButton =
          gallery.querySelector('[id^="GalleryThumbnails"] .thumbnail[aria-current="true"]') ||
          gallery.querySelector('[id^="GalleryThumbnails"] button[aria-current="true"]');

        if (activeThumbnailButton) {
          const thumbnailItem = activeThumbnailButton.closest('[data-target]');
          if (thumbnailItem && thumbnailItem.dataset.target) {
            return thumbnailItem.dataset.target;
          }
        }

        const slider =
          (sectionId && document.getElementById('Slider-Gallery-' + sectionId)) ||
          gallery.querySelector('[id^="Slider-Gallery"]');

        if (slider) {
          const slides = Array.from(slider.querySelectorAll('[data-media-id]')).filter((slide) => {
            const rect = slide.getBoundingClientRect();
            const style = window.getComputedStyle(slide);

            return (
              rect.width > 0 &&
              rect.height > 0 &&
              style.display !== 'none' &&
              style.visibility !== 'hidden'
            );
          });

          if (slides.length) {
            const center = slider.scrollLeft + slider.clientWidth / 2;
            let closestSlide = slides[0];
            let closestDistance = Infinity;

            slides.forEach((slide) => {
              const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
              const distance = Math.abs(center - slideCenter);

              if (distance < closestDistance) {
                closestDistance = distance;
                closestSlide = slide;
              }
            });

            const closestMediaId = closestSlide.getAttribute('data-media-id');
            if (closestMediaId) return closestMediaId;
          }
        }

        return '';
      }

      resolveTargetMediaId(opener) {
        /*
          DiYoZone Fix:
          优先读取当前 gallery 里真正 active 的图片。
          这样 fullscreen 一打开就是当前选中的图片。
        */

        const activeGalleryMediaId = this.getActiveMediaIdFromGallery(opener || this.openedBy);
        if (activeGalleryMediaId) return activeGalleryMediaId;

        const openerMediaId = this.getMediaIdFromElement(opener || this.openedBy);
        if (openerMediaId) return openerMediaId;

        return '';
      }

      findMediaById(targetId) {
        if (!targetId || !this.mediaList || !this.mediaList.length) return null;

        const cleanTargetId = String(targetId).trim();

        const exactMatch = this.mediaList.find((media) => {
          return media.getAttribute('data-media-id') === cleanTargetId;
        });

        if (exactMatch) return exactMatch;

        const rawTargetId = this.getRawMediaId(cleanTargetId);

        return (
          this.mediaList.find((media) => {
            const mediaId = media.getAttribute('data-media-id') || '';
            return this.getRawMediaId(mediaId) === rawTargetId;
          }) || null
        );
      }

      isFullscreenArrowTarget(target) {
        if (!target || !target.closest) return false;

        return !!(
          target.closest('.fullscreen-arrows') ||
          target.closest('.fs-arrow') ||
          target.closest('[data-fullscreen-prev]') ||
          target.closest('[data-fullscreen-next]')
        );
      }

      bindBackgroundClose() {
        if (!this.container) return;

        this.container.addEventListener('click', (event) => {
          if (event.button && event.button !== 0) return;

          if (this.isFullscreenArrowTarget(event.target)) return;

          const clickedFullscreenWrapper =
            event.target.classList &&
            event.target.classList.contains('fullscreen-media-wrapper');

          const clickedContainer = event.target === this.container;

          if (clickedFullscreenWrapper || clickedContainer) {
            this.hide();
          }
        });
      }

      bindRightClickProtection() {
        const protectRightClick = (event) => {
          const isRightClick =
            event.type === 'contextmenu' ||
            event.button === 2 ||
            event.which === 3;

          if (!isRightClick) return;

          const isInsideModal = this.contains(event.target);
          if (!isInsideModal) return;

          const isControl =
            event.target.closest &&
            (
              event.target.closest('.product-media-modal__toggle') ||
              event.target.closest('.fullscreen-arrows') ||
              event.target.closest('.fs-arrow') ||
              event.target.closest('[data-fullscreen-prev]') ||
              event.target.closest('[data-fullscreen-next]')
            );

          if (isControl) return;

          /*
            只阻止冒泡，不 preventDefault。
            保留 Chrome 原生图片右键菜单，同时不退出 fullscreen。
          */
          event.stopPropagation();
        };

        ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'auxclick', 'contextmenu'].forEach((eventName) => {
          this.addEventListener(eventName, protectRightClick, true);

          if (this.container) {
            this.container.addEventListener(eventName, protectRightClick, true);
          }
        });
      }

      collectMediaList() {
        if (!this.container) {
          this.mediaList = [];
          return;
        }

        this.mediaList = Array.from(this.container.children).filter((element) => {
          return element.hasAttribute('data-media-id');
        });
      }

      startInstantScrollMode(duration) {
        if (!this.container) return;

        const keepDuration = duration || 260;

        if (!this.instantScrollSnapshot) {
          this.instantScrollSnapshot = {
            scrollBehaviorValue: this.container.style.getPropertyValue('scroll-behavior'),
            scrollBehaviorPriority: this.container.style.getPropertyPriority('scroll-behavior'),
            scrollSnapTypeValue: this.container.style.getPropertyValue('scroll-snap-type'),
            scrollSnapTypePriority: this.container.style.getPropertyPriority('scroll-snap-type')
          };
        }

        window.clearTimeout(this.instantScrollRestoreTimer);

        /*
          核心修复：
          打开 fullscreen 的第一次定位，强制关闭 smooth scroll 和 scroll snap。
          这样不会看到“滑过去”的动画。
        */
        this.container.style.setProperty('scroll-behavior', 'auto', 'important');
        this.container.style.setProperty('scroll-snap-type', 'none', 'important');

        this.instantScrollRestoreTimer = window.setTimeout(() => {
          this.restoreInstantScrollMode();
        }, keepDuration);
      }

      restoreInstantScrollMode() {
        if (!this.container || !this.instantScrollSnapshot) return;

        const snapshot = this.instantScrollSnapshot;

        if (snapshot.scrollBehaviorValue) {
          this.container.style.setProperty(
            'scroll-behavior',
            snapshot.scrollBehaviorValue,
            snapshot.scrollBehaviorPriority || ''
          );
        } else {
          this.container.style.removeProperty('scroll-behavior');
        }

        if (snapshot.scrollSnapTypeValue) {
          this.container.style.setProperty(
            'scroll-snap-type',
            snapshot.scrollSnapTypeValue,
            snapshot.scrollSnapTypePriority || ''
          );
        } else {
          this.container.style.removeProperty('scroll-snap-type');
        }

        this.instantScrollSnapshot = null;
      }

      instantScrollToMedia(media) {
        if (!this.container || !media) return;

        this.startInstantScrollMode(360);

        try {
          this.container.scrollTo({
            left: media.offsetLeft,
            behavior: 'auto'
          });
        } catch (error) {
          this.container.scrollLeft = media.offsetLeft;
        }

        this.container.scrollLeft = media.offsetLeft;
      }

      scheduleInstantInitialPosition(media) {
        if (!media) return;

        this.instantScrollToMedia(media);

        window.requestAnimationFrame(() => {
          this.instantScrollToMedia(media);

          window.requestAnimationFrame(() => {
            this.instantScrollToMedia(media);
          });
        });

        window.setTimeout(() => {
          this.instantScrollToMedia(media);
        }, 20);

        window.setTimeout(() => {
          this.instantScrollToMedia(media);
        }, 80);

        window.setTimeout(() => {
          this.instantScrollToMedia(media);
        }, 160);
      }

      prepareActiveMediaBeforeOpen(opener) {
        this.collectMediaList();

        if (!this.mediaList.length) {
          this.pendingActiveMedia = null;
          this.currentIndex = 0;
          return;
        }

        const targetId = this.resolveTargetMediaId(opener);
        const activeMedia = this.findMediaById(targetId) || this.mediaList[0];

        this.mediaList.forEach((element) => {
          element.classList.remove('active');
        });

        activeMedia.classList.add('active');
        this.currentIndex = this.mediaList.indexOf(activeMedia);
        this.pendingActiveMedia = activeMedia;

        this.loadDeferredMedia(activeMedia);

        /*
          在 modal 显示前先尝试定位。
          如果此时 display:none 导致 offset 不稳定，show 后还会继续硬定位。
        */
        this.instantScrollToMedia(activeMedia);
      }

      hide() {
        super.hide();
        this.removeInjectedArrowsOnly();
      }

      show(opener) {
        if (opener) {
          this.openedBy = opener;
        }

        this.prepareActiveMediaBeforeOpen(opener || this.openedBy);

        super.show(opener);

        this.showActiveMedia(opener || this.openedBy, true, true);

        this.ensureFullscreenArrows();
        this.bindFullscreenArrows();
      }

      scrollToMedia(media, behavior) {
        if (!this.container || !media) return;

        const scrollBehavior = behavior || 'auto';

        try {
          this.container.scrollTo({
            left: media.offsetLeft,
            behavior: scrollBehavior
          });
        } catch (error) {
          this.container.scrollLeft = media.offsetLeft;
        }
      }

      showActiveMedia(opener, usePendingMedia, instant) {
        if (!this.mediaList || !this.mediaList.length) {
          this.collectMediaList();
        }

        if (!this.mediaList || !this.mediaList.length) return;

        let activeMedia = null;

        if (usePendingMedia && this.pendingActiveMedia) {
          activeMedia = this.pendingActiveMedia;
        } else {
          const targetId = this.resolveTargetMediaId(opener);
          activeMedia = this.findMediaById(targetId) || this.mediaList[0];
        }

        if (!activeMedia) return;

        this.mediaList.forEach((element) => {
          element.classList.remove('active');
        });

        activeMedia.classList.add('active');
        this.currentIndex = this.mediaList.indexOf(activeMedia);

        this.loadDeferredMedia(activeMedia);

        if (instant) {
          this.scheduleInstantInitialPosition(activeMedia);
        } else {
          this.scrollToMedia(activeMedia, 'auto');
        }
      }

      loadDeferredMedia(media) {
        if (!media) return;

        const activeMediaTemplate = media.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        const deferredMedia = media.matches('deferred-media')
          ? media
          : media.querySelector('deferred-media');

        if (
          deferredMedia &&
          activeMediaContent &&
          (
            activeMediaContent.querySelector('.js-youtube') ||
            activeMediaContent.querySelector('.js-vimeo') ||
            activeMediaContent.querySelector('video') ||
            activeMediaContent.querySelector('model-viewer')
          )
        ) {
          deferredMedia.loadContent();
        }
      }

      ensureFullscreenArrows() {
        if (!this.mediaList || this.mediaList.length <= 1) return;

        /*
          如果 main-product.liquid 已经有 fullscreen-arrows，
          就直接使用，不重复生成。
        */
        if (this.querySelector('.fullscreen-arrows')) return;

        const arrowContainer = document.createElement('div');
        arrowContainer.className = 'fullscreen-arrows';
        arrowContainer.setAttribute('data-diyozone-injected-arrows', 'true');
        arrowContainer.setAttribute('aria-label', 'Product media navigation');

        arrowContainer.innerHTML = `
          <button type="button" class="fs-arrow fs-prev" aria-label="Previous image" data-fullscreen-prev>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M15 18L9 12L15 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>

          <button type="button" class="fs-arrow fs-next" aria-label="Next image" data-fullscreen-next>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M9 18L15 12L9 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        `;

        const dialogWrapper = this.querySelector('.product-media-modal__dialog') || this;
        dialogWrapper.appendChild(arrowContainer);
      }

      bindFullscreenArrows() {
        const arrowContainer = this.querySelector('.fullscreen-arrows');
        const prevBtn = this.querySelector('[data-fullscreen-prev], .fs-prev');
        const nextBtn = this.querySelector('[data-fullscreen-next], .fs-next');

        if (!arrowContainer || (!prevBtn && !nextBtn)) return;

        if (arrowContainer.dataset.diyozoneProductModalArrowsBound === 'true') return;
        arrowContainer.dataset.diyozoneProductModalArrowsBound = 'true';

        const stopOnly = (event) => {
          if (!this.isFullscreenArrowTarget(event.target)) return;

          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }
        };

        [
          'pointerdown',
          'pointerup',
          'mousedown',
          'mouseup',
          'touchstart',
          'touchend',
          'auxclick',
          'contextmenu'
        ].forEach((eventName) => {
          arrowContainer.addEventListener(eventName, stopOnly, true);

          if (prevBtn) {
            prevBtn.addEventListener(eventName, stopOnly, true);
          }

          if (nextBtn) {
            nextBtn.addEventListener(eventName, stopOnly, true);
          }
        });

        const handlePrev = (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          this.handleArrowClick('prev');
        };

        const handleNext = (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (typeof event.stopImmediatePropagation === 'function') {
            event.stopImmediatePropagation();
          }

          this.handleArrowClick('next');
        };

        if (prevBtn) {
          prevBtn.addEventListener('click', handlePrev, true);
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', handleNext, true);
        }
      }

      removeInjectedArrowsOnly() {
        const injectedArrows = this.querySelectorAll('.fullscreen-arrows[data-diyozone-injected-arrows="true"]');

        injectedArrows.forEach((arrows) => {
          arrows.remove();
        });
      }

      getCurrentIndexFromScroll() {
        if (!this.container || !this.mediaList || !this.mediaList.length) {
          return this.currentIndex || 0;
        }

        const center = this.container.scrollLeft + this.container.clientWidth / 2;
        let closestIndex = this.currentIndex || 0;
        let closestDistance = Infinity;

        this.mediaList.forEach((media, index) => {
          const mediaCenter = media.offsetLeft + media.offsetWidth / 2;
          const distance = Math.abs(center - mediaCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        return closestIndex;
      }

      handleArrowClick(direction) {
        if (!this.mediaList || !this.mediaList.length) {
          this.collectMediaList();
        }

        if (!this.mediaList || !this.mediaList.length) return;

        this.currentIndex = this.getCurrentIndexFromScroll();

        if (direction === 'next') {
          this.currentIndex =
            this.currentIndex >= this.mediaList.length - 1
              ? 0
              : this.currentIndex + 1;
        } else {
          this.currentIndex =
            this.currentIndex <= 0
              ? this.mediaList.length - 1
              : this.currentIndex - 1;
        }

        const targetMedia = this.mediaList[this.currentIndex];
        if (!targetMedia) return;

        this.mediaList.forEach((element) => {
          element.classList.remove('active');
        });

        targetMedia.classList.add('active');

        this.loadDeferredMedia(targetMedia);

        /*
          箭头切换可以保留 smooth。
          只有打开 fullscreen 的第一次定位才强制 instant。
        */
        this.scrollToMedia(targetMedia, 'smooth');
      }
    }
  );
}